import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModel,
  DataModelBuilder,
  SerializationContext,
  SerializedData,
  isSameData,
  isSameList,
} from '../../dto/dto.js';
import { LocalizedString } from '../localized-string.js';
import { PhotoReference } from '../file-reference/photo-reference.js';
import { IDataWithLocalizedName } from '../../../contracts/localized-data.js';
import { VersionRange } from '../../core/version-range.js';

export class MenuCategory extends DataModel implements IDataWithLocalizedName {
  static readonly TYPE_ID: string = 'a8a152a50eb1472597a7f3b99019e7e6';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly photo: PhotoReference | null,
    public readonly items: string[],
    public readonly featured: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return MenuCategory.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, MenuCategory.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): MenuCategory {
    return new MenuCategory(
        MapUtils.get<string>(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'photo')),
        MapUtils.getList<string>(map, 'items', (value) => value as string),
        MapUtils.get<boolean>(map, 'featured'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'photo': context.toDataOrNull(this.photo),
      'items': this.items,
      'featured': this.featured,
    };
  }

  clone(): this {
    return new MenuCategory(
        this.uniqueId,
        this.names.clone(),
        this.photo?.clone() ?? null,
        this.items,
        this.featured,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId == this.uniqueId &&
       isSameData(other.names, this.names) &&
       isSameData(other.photo, this.photo) &&
       isSameList<string>(other.items, this.items) &&
       other.featured === this.featured;
  }
}

export class MenuCategoryBuilder extends DataModelBuilder<MenuCategory> {
  build(context: SerializationContext, map: SerializedData): MenuCategory {
    return MenuCategory.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(MenuCategory.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(MenuCategory.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the parameter `featured` of type `bool` was introduced.
    // Featured category stays always at index 0 in the category list and it can
    // not be deleted. This category acts as a "home page" on the menu UI. Once
    // set this value should not be change to avoid breaking the UI.
    if(version < 2) {
      map['featured'] ??= false;

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
